// MultiSelectPersonaBar.tsx
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { FiPlusCircle } from 'react-icons/fi';

import React, { memo } from 'react';
import { ConversationType } from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';

// Define types for the component
interface PersonaType {
  id: string;
  name: string;
}

interface MultiSelectPersonaBarProps {
  selectedPersonas: PersonaType[];
  onRemovePersona: (id: string) => void;
  onAddPersona: () => void;
  maxSelections?: number;
  type: ConversationType;
}

const MultiSelectPersonaBar: React.FC<MultiSelectPersonaBarProps> = ({
  selectedPersonas,
  onRemovePersona,
  onAddPersona,
  maxSelections = 6,
  type,
}) => {
  const getTagColor = (idx: number): string => {
    if (idx % 3 === 0) {
      return '#F9D5FF';
    }
    if (idx % 3 === 1) {
      return '#E3F9B9';
    }
    return '#D9C3F6';
  };

  return (
    <Stack spacing={2} width="full">
      <Box
        borderWidth="1px"
        borderRadius="lg"
        borderColor="gray.200"
        bg={HelivanColor.background.lightestGrey}
        p={1.5}
        width="100%"
        position="relative"
        onClick={onAddPersona}
        cursor="pointer"
      >
        <Flex
          overflowX="auto"
          width={'full'}
          alignItems="center"
          justifyContent="space-between"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          <HStack spacing={2}>
            {selectedPersonas.map((persona, idx) => (
              <Tag
                key={persona.id}
                size="sm"
                borderRadius="4px"
                variant="solid"
                bg={getTagColor(idx)}
                color="black"
                padding="2px 4px"
                mr={2}
              >
                <TagLabel>
                  <HelivanText variant="smallBody" colorOverride="black">
                    {persona.name}
                  </HelivanText>
                </TagLabel>
                <TagCloseButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemovePersona(persona.id);
                  }}
                  _hover={{ bg: 'rgba(0, 0, 0, 0.1)' }}
                />
              </Tag>
            ))}
          </HStack>

          <IconButton
            aria-label="Toggle persona selection"
            icon={<FiPlusCircle size={20} />}
            variant="ghost"
            borderRadius="full"
            size="sm"
            _hover={{ bg: undefined }}
          />
        </Flex>
      </Box>

      <HelivanText variant="smallBody">
        {type === ConversationType.Persona
          ? 'Select a persona to 1-1. To select multiple, create a panel instead.'
          : type === ConversationType.Panel
            ? 'Select between 2-6 Personas.'
            : 'Select a set of demographics. You may only select up to one value per category.'}
      </HelivanText>
    </Stack>
  );
};

// Memoize the component for better performance
export default memo(MultiSelectPersonaBar);
